import React, { useState, useEffect, useCallback } from 'react';
import { differenceInDays } from 'date-fns';
import PropTypes from 'prop-types';
import { VaDate } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { parseDateWithTemplate } from '../../../shared/utils/dates';
import { isValidDate } from '../utils';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import SubTaskContainerHeader from '../SubTaskContainerHeader';
import pageNames from './pageNames';
import { FORM_STATUS_BDD, SAVED_SEPARATION_DATE } from '../../constants';
import {
  trackSubtaskStepView,
  trackSeparationDateEntered,
  trackSubtaskValidationError,
} from '../../utils/tracking/features/subtaskTracking';

const saveDischargeDate = (date, isBDD) => {
  if (date) {
    window.sessionStorage.setItem(SAVED_SEPARATION_DATE, date);
    // this flag helps maintain the correct form title within a session
    window.sessionStorage.setItem(FORM_STATUS_BDD, isBDD ? 'true' : 'false');
  } else {
    window.sessionStorage.removeItem(SAVED_SEPARATION_DATE);
    window.sessionStorage.removeItem(FORM_STATUS_BDD);
  }
};

const heading =
  'What’s the date (or expected date) of your release from active duty?';

const label = 'Service end date';

const hint =
  'Select a month. Enter 1 or 2 digits for the day and 4 digits for the year.';

// Figure out which page to go to based on the date entered
const findNextPage = date => {
  const dateDischarge = parseDateWithTemplate(date);
  const dateToday = new Date();
  const differenceBetweenDatesInDays =
    differenceInDays(dateDischarge, dateToday) + 1;

  if (differenceBetweenDatesInDays < 0) {
    saveDischargeDate();
    return null;
  }

  if (differenceBetweenDatesInDays < 90) {
    saveDischargeDate(date, false);
    return pageNames.fileClaimEarly;
  }

  if (differenceBetweenDatesInDays <= 180) {
    saveDischargeDate(date, true);
    return pageNames.fileBDD;
  }

  saveDischargeDate();
  return pageNames.unableToFileBDD;
};

const validate = ({ radDate }) => isValidDate(radDate);

const getNextPage = data => {
  const { radDate } = data;
  const nextPage = isValidDate(radDate) ? findNextPage(radDate) : null;
  return nextPage || '';
};

/**
 * RAD = Release from active duty
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const Rad = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();
  const [errorMessage, setErrorMessage] = useState(null);

  // ──────────────────────────────────────────────
  // STEP VIEW TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    trackSubtaskStepView({
      stepName: pageNames.rad,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkValid = useCallback(value => {
    let message = null;

    if (!isValidDate(value)) {
      message = 'Enter a valid future separation date';
    }

    setErrorMessage(message);
  }, []);

  useEffect(() => {
    checkValid(data.radDate);
  }, [checkValid, data.radDate]);

  // ──────────────────────────────────────────────
  // ERROR TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (error && errorMessage) {
      trackSubtaskValidationError(
        pageNames.rad,
        'separation-date',
        errorMessage,
      );
    }
  }, [error, errorMessage]);

  const onChange = event => {
    const value = event.target.value || '';
    saveDischargeDate();
    setPageData({ radDate: value });
    checkValid(value);
  };

  return (
    <div id={pageNames.rad} className="clearfix">
      <SubTaskContainerHeader title={heading} />
      <VaDate
        required
        label={label}
        hint={hint}
        className="vads-u-margin-top--0"
        onDateChange={onChange}
        name="discharge-date"
        value={data.radDate}
        error={error ? errorMessage : null}
      />
    </div>
  );
};

Rad.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.rad,
  component: Rad,
  validate,
  back: pageNames.start,
  next: getNextPage,
  // ──────────────────────────────────────────────
  // USER INPUT TRACKING
  // ──────────────────────────────────────────────
  onContinue: data => {
    if (data.radDate && isValidDate(data.radDate)) {
      const dateDischarge = parseDateWithTemplate(data.radDate);
      const dateToday = new Date();
      const daysUntilSeparation =
        differenceInDays(dateDischarge, dateToday) + 1;
      trackSeparationDateEntered({ daysUntilSeparation });
    }
  },
};
